import React, { useContext, useEffect, useState } from 'react';
import {
  Grid, makeStyles, Typography, Box, LinearProgress, Button, CircularProgress, AccordionDetails,
  AccordionSummary, Accordion,
} from '@material-ui/core';
import {
  ExpandMore,
} from '@material-ui/icons';
import { FormattedMessage } from 'react-intl';
import { useMsal } from '@azure/msal-react';
import Alert from '@material-ui/lab/Alert';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';
import { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import { maxWidth, isDevAuthBypass, DEV_BYPASS_EMAIL } from '../config/config';
import { LoggedIn, LoggedOut } from './Common/AuthTemplate';
import FollowUps from './ScreenWelcome/FollowUps';
import SelectSpecialist from './ScreenWelcome/SelectSpecialist';
import SelectService from './ScreenWelcome/SelectService';
import FutureAppointments from './ScreenWelcome/FutureAppointments';
import api from '../config/api';
import AppContext from '../state/AppContext';
import { fetchStaticToken, swallowMsalErrors } from '../helpers/authentication';
import { loginRequest } from '../config/authConfig';
import LinearProgressValidatingCustomer from './Common/LinearProgressValidatingCustomer';
import isLoadingClinicsState, { isLoadingRegionsState } from '../state/apiRequestStatus';
import {
  clinicsState, regionsState,
  servicesState,
  therapistsState,
  validatedCustomerState,
  devBypassEmailState,
} from '../state/apiData';
import { selectedUITherapistState } from '../state/selected';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(11),
  },
  colLeftWrapper: {
    display: 'flex',
    justifyContent: 'center',
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-end',
    },
  },
  colLeft: {
    flex: '1 1 100%',
    maxWidth: (maxWidth / 2),
    [theme.breakpoints.up('md')]: {
      minHeight: '70vh',
    },
  },
  colLeftInner: {
    paddingTop: theme.spacing(3),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(6),
    },
  },
  colRightWrapper: {
    display: 'flex',
    backgroundColor: theme.palette.grey[100],
    justifyContent: 'center',
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-start',
    },
  },
  colRight: {
    flex: '1 1 100%',
    maxWidth: (maxWidth / 2),
    [theme.breakpoints.up('md')]: {
      minHeight: '70vh',
    },
  },
  colRightInner: {
    paddingTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(6),
    },
  },
}));

const ScreenWelcome = () => {
  const classes = useStyles();
  const {
    customerNeedsSignup,
  } = useContext(AppContext);

  const { urlTherapistId } = useParams();

  const {
    inProgress,
    instance,
  } = useMsal();

  const validatedCustomer = useRecoilValue(validatedCustomerState);
  const [therapists, setTherapists] = useRecoilState(therapistsState);
  const [services, setServices] = useRecoilState(servicesState);
  const setRegions = useSetRecoilState(regionsState);
  const setClinics = useSetRecoilState(clinicsState);
  const setIsLoadingClinics = useSetRecoilState(isLoadingClinicsState);
  const setIsLoadingRegions = useSetRecoilState(isLoadingRegionsState);
  const setSelectedUITherapist = useSetRecoilState(selectedUITherapistState);
  const setDevEmail = useSetRecoilState(devBypassEmailState);

  const [isFetchingServices, setIsFetchingServices] = useState(true);
  const [isFetchingTherapists, setIsFetchingTherapists] = useState(true);

  const fetchServices = async () => {
    const responseServices = await fetchStaticToken({
      url: api.services,
      method: 'GET',
    });

    if (responseServices.status === 200) {
      const rServices = await responseServices.json();
      setServices(rServices);
    }

    setIsFetchingServices(false);
  };

  const fetchTherapists = async () => {
    const responseTherapists = await fetchStaticToken({
      url: api.therapists,
      method: 'GET',
    });

    if (responseTherapists.status === 200) {
      const therapistsData = await responseTherapists.json();
      const rTherapists = therapistsData.map((t) => ({
        ...t,
        name: `${t.firstName} ${t.therapistName}`,
      }));
      setTherapists(rTherapists);

      if (urlTherapistId) {
        const matchedTherapist = rTherapists.find(
          (t) => parseInt(t.therapistId, 10) === parseInt(urlTherapistId, 10),
        );

        if (matchedTherapist) {
          setSelectedUITherapist(cloneDeep(matchedTherapist));
        }
      }
    }

    setIsFetchingTherapists(false);
  };

  const fetchClinics = async () => {
    setIsLoadingClinics(true);
    const responseClinics = await fetchStaticToken({
      url: api.clinics,
      method: 'GET',
    });

    if ([200, 201].includes(responseClinics.status)) {
      const clinicsData = await responseClinics.json();
      setClinics(clinicsData);
    }

    setIsLoadingClinics(false);
  };

  const fetchRegions = async () => {
    setRegions([]);
    setIsLoadingRegions(true);
    const responseRegions = await fetchStaticToken({
      url: api.regions,
      method: 'GET',
    });

    if ([200, 201].includes(responseRegions.status)) {
      const regionsData = await responseRegions.json();
      setRegions(regionsData);
    }

    setIsLoadingRegions(false);
  };

  useEffect(() => {
    fetchServices();
    fetchTherapists();
    fetchClinics();
    fetchRegions();
  }, []);

  const onLoginClick = () => {
    if (isDevAuthBypass) {
      setDevEmail(DEV_BYPASS_EMAIL);
    } else {
      instance.loginPopup(loginRequest).catch(swallowMsalErrors);
    }
  };

  return (
    <div className={classes.container}>
      <Grid
        container
        justifyContent="center"
      >
        <Grid
          className={classes.colLeftWrapper}
          item
          xs={12}
          md={6}
        >
          <div
            className={classes.colLeft}
          >
            <div
              className={classes.colLeftInner}
            >
              <LoggedOut>
                <Box mb={1}>
                  <Typography variant="h3">
                    <FormattedMessage
                      id="Home.welcomeTitle"
                      values={{
                        b: (...chunks) => <b>{chunks}</b>,
                      }}
                    />
                  </Typography>
                </Box>
                <Box mb={3}>
                  <Typography variant="body1">
                    <FormattedMessage
                      id="Home.welcomeLine1"
                      values={{
                        b: (...chunks) => <b>{chunks}</b>,
                      }}
                    />
                  </Typography>
                </Box>
                <Alert severity="info">
                  <Typography variant="body1">
                    <FormattedMessage
                      id="Home.welcomeLine2"
                      values={{
                        b: (...chunks) => <b>{chunks}</b>,
                      }}
                    />
                  </Typography>
                </Alert>
              </LoggedOut>
              <LoggedIn>
                <Box mb={1}>
                  <Typography variant="h3">
                    <FormattedMessage
                      id="Home.welcomeLoggedInTitle"
                      values={{
                        b: (...chunks) => <b>{chunks}</b>,
                      }}
                    />
                  </Typography>
                </Box>
                <br />
                <Box mb={3}>
                  <Typography variant="body1">
                    <FormattedMessage
                      id="Home.welcomeLoggedInLine1"
                      values={{
                        b: (...chunks) => <b>{chunks}</b>,
                      }}
                    />
                  </Typography>
                </Box>

              </LoggedIn>
              {isFetchingServices || isFetchingTherapists
                ? <Box pt={3}><LinearProgress /></Box>
                : (
                  <>
                    <SelectSpecialist therapists={therapists} />
                    <Box pt={3}><SelectService services={services} /></Box>
                  </>
                )}
              <Box mt={4} mb={4}>
                <Box mb={2}>
                  <Typography variant="h5">
                    <FormattedMessage id="Home.faqTitle" />
                  </Typography>
                </Box>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                  >
                    <Typography className={classes.heading}>
                      <FormattedMessage
                        id="Home.accordion1title"
                        values={{
                          p: (...chunks) => <p>{chunks}</p>,
                          br: <br />,
                        }}
                      />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography component="div">
                      <FormattedMessage
                        id="Home.accordion1text"
                        values={{
                          p: (...chunks) => <p>{chunks}</p>,
                          b: (...chunks) => <b>{chunks}</b>,
                          br: <br />,
                        }}
                      />
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                  >
                    <Typography className={classes.heading}>
                      <FormattedMessage
                        id="Home.accordion2title"
                        values={{
                          p: (...chunks) => <p>{chunks}</p>,
                          br: <br />,
                        }}
                      />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography component="div">
                      <FormattedMessage
                        id="Home.accordion2text"
                        values={{
                          p: (...chunks) => <p>{chunks}</p>,
                          b: (...chunks) => <b>{chunks}</b>,
                          br: <br />,
                        }}
                      />
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                  >
                    <Typography className={classes.heading}>
                      <FormattedMessage
                        id="Home.accordion3title"
                        values={{
                          p: (...chunks) => <p>{chunks}</p>,
                          br: <br />,
                        }}
                      />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography component="div">
                      <FormattedMessage
                        id="Home.accordion3text"
                        values={{
                          p: (...chunks) => <p>{chunks}</p>,
                          b: (...chunks) => <b>{chunks}</b>,
                          br: <br />,
                        }}
                      />
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </div>
          </div>
        </Grid>
        <Grid
          className={classes.colRightWrapper}
          item
          xs={12}
          md={6}
        >
          <div
            className={classes.colRight}
          >
            <div
              className={classes.colRightInner}
            >
              <Box mb={4}>
                <LoggedOut>
                  <Box mb={2}>
                    <Typography variant="h3">
                      <FormattedMessage id="Home.loginTitle" />
                    </Typography>
                  </Box>
                  <Box mb={3}>
                    <Typography>
                      <FormattedMessage id="Home.loginSubtitle" />
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={onLoginClick}
                    disabled={inProgress === 'login'}
                  >
                    {inProgress === 'login'
                      ? <CircularProgress size={20} />
                      : <FormattedMessage id="Home.login" />}
                  </Button>
                </LoggedOut>
                <LoggedIn>
                  <LinearProgressValidatingCustomer />
                  {validatedCustomer
                    && (
                      <>
                        <FutureAppointments />
                        <FollowUps />
                      </>
                    )}
                  {customerNeedsSignup
                    && (
                      <Box mt={3}>
                        <Alert severity="error">
                          <FormattedMessage id="Home.errorCustomerNeedsSignup" />
                        </Alert>
                      </Box>
                    )}
                </LoggedIn>
              </Box>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

ScreenWelcome.propTypes = {};

export default ScreenWelcome;
